import { Filter } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ColumnFilterGroup } from './types';

// Parity: HeaderCellFilter (BaseListScreen.kt:411-540) — per-column multi-select dropdown.
// The trigger is an icon-only funnel so the column's header label (rendered separately in
// DataGrid) is NOT printed twice (previously showed "Type Type"). A count badge appears when
// filters are active; the funnel highlights while the popover holds a selection.
export function ColumnFilter({
  group,
  selected,
  onChange,
}: {
  group: ColumnFilterGroup;
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  // multiSelect defaults to true. Single-select (multiSelect: false) replaces the selection — used
  // where the server accepts one value (e.g. moderation SearchRequestDTO.contentType/reportType).
  const multi = group.multiSelect !== false;
  const active = selected.length > 0;
  const toggle = (key: string, on: boolean) => {
    if (multi) onChange(on ? [...selected, key] : selected.filter((k) => k !== key));
    else onChange(on ? [key] : []);
  };

  return (
    <Popover>
      <PopoverTrigger
        aria-label={`Filter ${group.label}`}
        // Stop the click bubbling into the header's sort handler (DataGrid TableHead onClick).
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'inline-flex items-center gap-1 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground',
          active && 'text-primary',
        )}
      >
        <Filter className={cn('h-3.5 w-3.5', active && 'fill-current')} />
        {active && (
          <Badge variant="secondary" className="h-4 min-w-4 px-1 text-[10px] leading-none">
            {selected.length}
          </Badge>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-52 p-2" onClick={(e) => e.stopPropagation()}>
        <div className="mb-1 px-2 text-xs font-medium text-muted-foreground">{group.label}</div>
        {group.options.length === 0 ? (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">No options</div>
        ) : (
          group.options.map((opt) => (
            <label
              key={opt.key}
              className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
            >
              <Checkbox
                checked={selected.includes(opt.key)}
                onCheckedChange={(c) => toggle(opt.key, Boolean(c))}
              />
              <span>{opt.label}</span>
            </label>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
}
